import { useState, useCallback } from 'react';
import { api } from '../api/client';

/**
 * Custom hook for SKU validation and duplicate detection
 * Phase 3: Duplicate SKU Prevention
 */
export const useSKUValidation = (tenantId = 'tenant-123') => {
  const [validationState, setValidationState] = useState({
    isValidating: false,
    isValid: null,
    isDuplicate: false,
    existingProduct: null,
    error: null
  });

  const [conflictData, setConflictData] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  // Validate SKU in real-time
  const validateSKU = useCallback(async (sku, excludeProductId = null) => {
    if (!sku || sku.trim().length === 0) {
      setValidationState({
        isValidating: false,
        isValid: null,
        isDuplicate: false,
        existingProduct: null,
        error: null
      });
      return { valid: true, duplicate: false };
    }

    setValidationState(prev => ({ ...prev, isValidating: true, error: null }));

    try {
      const params = {
        tenant_id: tenantId,
        ...(excludeProductId && { exclude_product_id: excludeProductId })
      };

      const queryString = new URLSearchParams(params).toString();
      const response = await api.get(`/api/v1/products/validate-sku/${encodeURIComponent(sku)}?${queryString}`);

      const result = response.data;
      
      setValidationState({
        isValidating: false,
        isValid: result.valid,
        isDuplicate: result.duplicate,
        existingProduct: result.existing_product || null,
        error: null
      });

      return result;
      
    } catch (error) {
      console.error('SKU validation error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to validate SKU';
      
      setValidationState({
        isValidating: false,
        isValid: false,
        isDuplicate: false,
        existingProduct: null,
        error: errorMsg
      });

      return { valid: false, duplicate: false, error: errorMsg };
    }
  }, [tenantId]);

  // Handle duplicate conflict
  const handleDuplicateConflict = useCallback((errorData, originalSKU) => {
    setConflictData({
      ...errorData,
      original_sku: originalSKU
    });
    setShowDuplicateModal(true);
  }, []);

  // Handle conflict resolution
  const handleConflictResolution = useCallback(async (resolution) => {
    try {
      switch (resolution.action) {
        case 'open_existing':
          // Navigate to existing product
          return {
            action: 'navigate',
            productId: resolution.existing_product.id,
            message: `Navigating to existing product: ${resolution.existing_product.name}`
          };

        case 'rename_sku':
          // Return new SKU for form update
          return {
            action: 'rename',
            new_sku: resolution.new_sku,
            message: `SKU changed to: ${resolution.new_sku}`
          };

        case 'create_variant':
          // Create as product variant
          return {
            action: 'variant',
            parent_product: resolution.existing_product,
            message: `Creating variant of: ${resolution.existing_product.name}`
          };

        default:
          throw new Error('Unknown resolution action');
      }
    } catch (error) {
      throw new Error(`Failed to resolve conflict: ${error.message}`);
    }
  }, []);

  // Close duplicate modal
  const closeDuplicateModal = useCallback(() => {
    setShowDuplicateModal(false);
    setConflictData(null);
  }, []);

  // Clear validation state
  const clearValidation = useCallback(() => {
    setValidationState({
      isValidating: false,
      isValid: null,
      isDuplicate: false,
      existingProduct: null,
      error: null
    });
    setConflictData(null);
    setShowDuplicateModal(false);
  }, []);

  // Debounced validation (for real-time input)
  const debouncedValidate = useCallback((sku, delay = 500, excludeProductId = null) => {
    const timeoutId = setTimeout(() => {
      validateSKU(sku, excludeProductId);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [validateSKU]);

  return {
    // State
    validationState,
    conflictData,
    showDuplicateModal,

    // Actions
    validateSKU,
    debouncedValidate,
    handleDuplicateConflict,
    handleConflictResolution,
    closeDuplicateModal,
    clearValidation,

    // Computed properties
    isValidating: validationState.isValidating,
    isValid: validationState.isValid,
    isDuplicate: validationState.isDuplicate,
    existingProduct: validationState.existingProduct,
    validationError: validationState.error
  };
};